import React, { Component, ReactNode } from 'react';
import { MessageCircleOff, ChevronLeft } from 'lucide-react';
import { ErrorState } from '@/components/ui/ErrorState';
import { useAppTranslations } from '@/hooks/useAppTranslations';

interface MessagingErrorBoundaryProps {
  children: ReactNode;
  /** When true, renders a full-screen shell with a back button (for routes without MobileLayout) */
  showShell?: boolean;
}

interface MessagingErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  retryKey: number;
}

function MessagingErrorShell({ onRetry, onGoBack, error }: {
  onRetry: () => void;
  onGoBack: () => void;
  error: Error | null;
}) {
  const { t } = useAppTranslations();
  const es = t.ui.errorState;
  const d = es.domains.messages;

  return (
    <div className="flex flex-col h-screen bg-app">
      <div
        className="flex items-center px-4 gap-3 bg-white/80 backdrop-blur-md border-b border-slate-200/60 flex-shrink-0"
        style={{ paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))', paddingBottom: '14px' }}
      >
        <button
          onClick={onGoBack}
          className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="text-base font-semibold text-slate-900">{d.title}</span>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-8">
        <ErrorState
          icon={MessageCircleOff}
          domain="people"
          title={d.title}
          description={d.description}
          onRetry={onRetry}
          onGoBack={onGoBack}
          devError={error}
          className="w-full max-w-sm"
        />
      </div>
    </div>
  );
}

function MessagingErrorContent({ onRetry, onGoBack, error }: {
  onRetry: () => void;
  onGoBack: () => void;
  error: Error | null;
}) {
  const { t } = useAppTranslations();
  const d = t.ui.errorState.domains.messages;

  return (
    <div className="flex flex-col items-center justify-center px-6 py-16">
      <ErrorState
        icon={MessageCircleOff}
        domain="people"
        title={d.title}
        description={d.description}
        onRetry={onRetry}
        onGoBack={onGoBack}
        devError={error}
        className="w-full max-w-sm"
      />
    </div>
  );
}

export class MessagingErrorBoundary extends Component<MessagingErrorBoundaryProps, MessagingErrorBoundaryState> {
  constructor(props: MessagingErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, retryKey: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<MessagingErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('MessagingErrorBoundary:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState(prev => ({ hasError: false, error: null, retryKey: prev.retryKey + 1 }));
  };

  render() {
    if (this.state.hasError) {
      const goBack = () => window.history.back();

      if (this.props.showShell) {
        return (
          <MessagingErrorShell
            onRetry={this.handleRetry}
            onGoBack={goBack}
            error={this.state.error}
          />
        );
      }

      return (
        <MessagingErrorContent
          onRetry={this.handleRetry}
          onGoBack={goBack}
          error={this.state.error}
        />
      );
    }

    return (
      <React.Fragment key={this.state.retryKey}>
        {this.props.children}
      </React.Fragment>
    );
  }
}
